import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import {
  AlertController,
  IonicSafeString,
  ModalController,
  ToastController,
} from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { CancelableLoading } from "src/app/components/dialogs/cancelable-loading/cancelable-loading.component";

@Injectable({
  providedIn: "root",
})
export class AlertService {

  // Used to show open invitations alert only once. Resets on login and page reload.
  openInvitationsNeverShown = true;

  constructor(
    private alertController: AlertController,
    private modalController: ModalController,
    private translateService: TranslateService,
    private toastController: ToastController,
  ) { }

  /**
   * Presents the cancelable loading modal and adds an `ion-modal` modal to the document.
   *
   * This method creates a loading modal using the `CancelableLoading` component and presents it.
   *
   * @param loadingOptions - Optional configuration options for the loading modal (`CancelableLoadingOptions`).
   * @returns A Promise that resolves when the loading modal has been presented.
   *
   * @example
   * await alertService.presentLoading({
   *   cancelFn: () => { console.log('Loading cancelled'); },
   *   cancelableAfterMillis: 3000,
   *   confirmCancellation: true,
   *   cnfHeader: 'EXAMPLE.EXAMPLE_CONFIRMATION',
   *   translateCnfHeader: true,
   *   loadingMsg: 'Custom download message'
   * });
   */
  public async presentLoading(loadingOptions?: CancelableLoadingOptions) {
    const id = `cancelable-loading-${CancelableLoading.randomHexString(8)}`;
    const loadingProps = {id, inlineMode: false, ...loadingOptions};
    const loadingModal = await this.modalController.create(
      {
        component: CancelableLoading,
        cssClass: "cancelable-loading",
        backdropDismiss: false,
        componentProps: loadingProps,
        id: id,
      });

    return await loadingModal.present();
  }

  /**
   * Dismisses the currently presented loading modal.
   *
   * This method dismisses the loading modal by calling the modalController's dismiss method.
   * Any errors during dismissal are caught silently.
   *
   * @returns A Promise that resolves when the loading modal has been dismissed.
   *
   * @example
   * await alertService.dismissLoading();
   */
  public async dismissLoading() {
    this.modalController.dismiss().catch();
  }

  /**
   * @method presentToast
   * @param message The message which will be presented on the toast. Has to be inside the translations file.
   *
   * Presents a toast with custom text.
   */
  public async presentToastWithoutClosing(message: string) {
    try {
      await this.toastController.dismiss();
    } catch (e) { }
    const toast = await this.toastController.create({
      message: this.translateService.instant(message),
      position: "top",
      buttons: [
        {
          icon: "close-circle",
          role: "cancel",
          handler: () => { },
        },
      ],
    });
    toast.present();
  }

  /**
   * @method presentToast
   * @param message The message which will be presented on the toast. Has to be inside the translations file.
   *
   * Presents a toast with custom text.
   */
  public async presentToast(message: string) {
    try {
      await this.toastController.dismiss();
    } catch (e) { }
    const toast = await this.toastController.create({
      message: this.translateService.instant(message),
      duration: 5000,
      position: "top",
      buttons: [
        {
          icon: "close-circle",
          role: "cancel",
          handler: () => { },
        },
      ],
    });
    toast.present();
  }

  public async dismissAllToasts() {
    try {
      await this.toastController.dismiss();
    } catch (e) { }
  }

  /**
   * error window
   */
  public async showError(title: string, errorText: string) {
    const alert = await this.alertController.create({
      header: this.translateService.instant(title),
      subHeader: this.translateService.instant(errorText),
      buttons: [this.translateService.instant("BUTTONS.OK")],
    });
    alert.present();
  }

  /**
   * error window with details
   */
  public async showErrorWithDetails(
    title: string,
    errorText: string,
    errorDetails: string | IonicSafeString,
  ) {
    const alert = await this.alertController.create({
      header: this.translateService.instant(title),
      subHeader: this.translateService.instant(errorText),
      message: errorDetails,
      buttons: [this.translateService.instant("BUTTONS.OK")],
    });
    alert.present();
  }

  /**
   * Info window
   */
  public async showChoice(
    title: string,
    infoText: string,
    button1: string,
    button2: string,
    callbackButton1: () => void,
    callbackButton2: () => void,
    interParams?: Object,
  ) {
    const alert = await this.alertController.create({
      header: this.translateService.instant(title),
      subHeader: this.translateService.instant(infoText, interParams),
      buttons: [
        {
          text: this.translateService.instant(button1),
          handler: () => {
            callbackButton1();
          },
          role: "cancel",
        },
        {
          text: this.translateService.instant(button2),
          handler: () => {
            callbackButton2();
          },
        },
      ],
    });
    await alert.present();
  }

  public async showOpenInvitations(router: Router) {
    if (this.openInvitationsNeverShown) {
      this.openInvitationsNeverShown = false;
      this.showChoice(
        "INVITATIONS.PENDING_INVITATION",
        "INVITATIONS.VIEW_INVITATION",
        "BUTTONS.NO",
        "BUTTONS.YES",
        () => {},
        () => { router.navigateByUrl("/invitations"); },
      );
    }
  }
}

/**
 * Interface for the configuration options of the CancelableLoading component.
 */
export interface CancelableLoadingOptions {
  /**
   * Callback function triggered when the user presses the cancel button.
   * Please note that setting a callback function is useless if you set `cancelIsDeactivated` to `true`!
   */
  cancelFn?: () => void;

  /**
   * Hides the cancel button of the loading dialog.
   * By default, this attribute will be set to `false`.
   * Please note that setting the attributes `cancelFn`, `cancelableAfterMillis`, `confirmCancellation`, `cnfHeader`, `translateCnfHeader`, `cnfSubHeader`, `translateCnfSubHeader`, `cnfMsg` or `translateCnfMsg` is useless if you set this attribute to `true`!
   */
  cancelIsDeactivated?: boolean;
  
  /**
   * Time in milliseconds after which the cancel button becomes visible.
   * If not specified the cancel button will be visible immediately in the loading dialog.
   * Please note that setting timeout is useless if you set `cancelIsDeactivated` to `true`!
   */
  cancelableAfterMillis?: number;

  /**
   * Whether to show a confirmation dialog before canceling.
   * The default value is `false`.
   * Please note that setting a cancellation confirmation is useless if you set `cancelIsDeactivated` to `true`!
   */
  confirmCancellation?: boolean;

  /**
   * Header text for the cancel confirmation dialog that is used in the `ion-alert` component.
   * For more information about the `ion-alert` component see: https://ionicframework.com/docs/api/alert
   * This attribute can hold either a single `string` or an array of strings (`string[]`).
   * The `translateCnfHeader` attribute determines whether the single `string` or specific indices of the string array should be translated.
   * If a string array is used, all marked i18n indices within `translateCnfHeader` will be translated and concatenated with the remaining strings.
   * If no value is specified, the i18n translation key `"GENERAL.SURE_CANCEL"` will be used as the default value.
   * Please note that setting a confirmation header is useless if you set `cancelIsDeactivated` to `true`!
   */
  cnfHeader?: string[] | string;

  /**
   * Indicates whether the cancel confirmation header or specific parts of it should be translated.
   * If `cnfHeader` is a single `string`, a `boolean` value determines whether the string should be translated.
   * If `cnfHeader` is an array of strings (`string[]`), a number array (`number[]`) must specify the indices of the i18n key strings that should be translated.
   * By default, this attribute will be set to `true` during initialization if `cnfHeader` is `"GENERAL.SURE_CANCEL"` and no explicit value was provided (`undefined`).
   * Please note that setting a confirmation header translation is useless if you set `cancelIsDeactivated` to `true`!
   */
  translateCnfHeader?: number[] | boolean;

  /**
   * Subheader text for the cancel confirmation dialog that is used in the `ion-alert` component.
   * For more information about the `ion-alert` component see: https://ionicframework.com/docs/api/alert
   * This attribute can hold either a single `string` or an array of strings (`string[]`).
   * The `translateCnfSubHeader` attribute determines whether the single `string` or specific indices of the string array should be translated.
   * If a string array is used, all marked i18n indices within `translateCnfSubHeader` will be translated and concatenated with the remaining strings.
   * Please note that setting a confirmation subheader is useless if you set `cancelIsDeactivated` to `true`!
   */
  cnfSubHeader?: string[] | string;

  /**
   * Indicates whether the cancel confirmation subheader or specific parts of it should be translated.
   * If `cnfSubHeader` is a single `string`, a `boolean` value determines whether the string should be translated.
   * If `cnfSubHeader` is an array of strings (`string[]`), a number array (`number[]`) must specify the indices of the i18n key strings that should be translated.
   * Please note that setting a confirmation subheader translation is useless if you set `cancelIsDeactivated` to `true`!
   */
  translateCnfSubHeader?: number[] | boolean;

  /**
   * Message text for the cancel confirmation dialog that is used in the `ion-alert` component.
   * For more information about the `ion-alert` component see: https://ionicframework.com/docs/api/alert
   * This attribute can hold either a single `string` or an array of strings (`string[]`).
   * The `translateCnfMsg` attribute determines whether the single `string` or specific indices of the string array should be translated.
   * If a string array is used, all marked i18n indices within `translateCnfMsg` will be translated and concatenated with the remaining strings.
   * Please note that setting a confirmation message is useless if you set `cancelIsDeactivated` to `true`!
   */
  cnfMsg?: string[] | string;

  /**
   * Indicates whether the cancel confirmation message or specific parts of it should be translated.
   * If `cnfMsg` is a single `string`, a `boolean` value determines whether the string should be translated.
   * If `cnfMsg` is an array of strings (`string[]`), a number array (`number[]`) must specify the indices of the i18n key strings that should be translated.
   * Please note that setting a confirmation message translation is useless if you set `cancelIsDeactivated` to `true`!
   */
  translateCnfMsg?: number[] | boolean;

  /**
   * Message text for the loading dialog.
   * This attribute can hold either a single `string` or an array of strings (`string[]`).
   * The `translateLoadingMsg` attribute determines whether the single `string` or specific indices of the string array should be translated.
   * If a string array is used, all marked i18n indices within `translateLoadingMsg` will be translated and concatenated with the remaining strings.
   * Please note that setting a confirmation message translation is useless if you set `cancelIsDeactivated` to `true`!
   */
  loadingMsg?: string[] | string;

  /**
   * Indicates whether the loading message or specific parts of it should be translated.
   * If `loadingMsg` is a single `string`, a `boolean` value determines whether the string should be translated.
   * If `loadingMsg` is an array of strings (`string[]`), a number array (`number[]`) must specify the indices of the i18n key strings that should be translated.
   * By default, this attribute will be set to `true` during initialization if `loadingMsg` is `"GENERAL.PLEASE_WAIT"` and no explicit value was provided (`undefined`).
   */
  translateLoadingMsg?: number[] | boolean;
}